import { useRef, useState } from 'react'
import { ILocationSuggestion } from '@/components/templates/search/interfaces'
import { PROPIEDAD_TYPE_URL_SLUG } from '@/components/templates/listaSeoV2/classicSearch.constants'

const SEARCH_ADDRESS_STORAGE_KEY = 'toctocSearchAddress'

const persistSearchAddress = (value: ILocationSuggestion | null) => {
	try {
		if (value) localStorage.setItem(SEARCH_ADDRESS_STORAGE_KEY, JSON.stringify(value))
		else localStorage.removeItem(SEARCH_ADDRESS_STORAGE_KEY)
	} catch {
		// localStorage no disponible: se ignora
	}
}

export function useLocationAutocomplete() {
	const [query, setQueryState] = useState('')
	const [suggestions, setSuggestions] = useState<ILocationSuggestion[]>([])
	const [loading, setLoading] = useState(false)
	const [selected, setSelected] = useState<ILocationSuggestion | null>(null)
	const debounceRef = useRef<ReturnType<typeof setTimeout>>()
	const abortRef = useRef<AbortController>()

	const setQuery = (value: string) => {
		setQueryState(value)
		setSelected(null)
		persistSearchAddress(null)
		clearTimeout(debounceRef.current)
		if (value.trim().length < 2) {
			setSuggestions([])
			return
		}
		debounceRef.current = setTimeout(async () => {
			abortRef.current?.abort()
			const controller = new AbortController()
			abortRef.current = controller
			setLoading(true)
			try {
				const baseApi = process.env.NEXT_PUBLIC_HOME_BASE_API
				const res = await fetch(
					`${baseApi}/search/location-autocomplete?q=${encodeURIComponent(value.trim())}`,
					{ signal: controller.signal },
				)
				const data = res.ok ? await res.json() : []
				setSuggestions(Array.isArray(data) ? data : [])
			} catch {
				setSuggestions([])
			}
			setLoading(false)
		}, 300)
	}

	// Restaura una selección previa (ej. leída de localStorage) sin re-disparar la persistencia.
	const restore = (s: ILocationSuggestion) => {
		setSelected(s)
		setQueryState(s.sublabel ? `${s.label}, ${s.sublabel}` : s.label)
	}

	const select = (s: ILocationSuggestion) => {
		setSelected(s)
		setQueryState(s.sublabel ? `${s.label}, ${s.sublabel}` : s.label)
		setSuggestions([])
		persistSearchAddress(s)
	}

	const clear = () => {
		setQueryState('')
		setSelected(null)
		setSuggestions([])
		persistSearchAddress(null)
	}

	const dismissSuggestions = () => {
		setSuggestions([])
	}

	const buildUrl = (params: { operacion: string; tipoPropiedad: string }): { url: string | null; error: string | null } => {
		if (!selected) return { url: null, error: 'Selecciona una ubicación de la lista de sugerencias.' }
		if (!selected.seoUrl) return { url: null, error: 'No pudimos encontrar la ruta para esta ubicación.' }
		const tipoSlug = PROPIEDAD_TYPE_URL_SLUG[params.tipoPropiedad] ?? params.tipoPropiedad
		return { url: `/${params.operacion}/${tipoSlug}/${selected.seoUrl}`, error: null }
	}

	return { query, setQuery, restore, suggestions, loading, selected, select, clear, dismissSuggestions, buildUrl }
}
